<template>
  <v-dialog :model-value="show" @update:model-value="closeDialog" max-width="800px" persistent scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Select Functions for Tools</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" variant="text" size="small">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text style="max-height: 70vh;">
        <div v-if="loadingFunctions" class="text-center">
          <v-progress-circular indeterminate color="primary" class="my-4"></v-progress-circular>
          <p>Loading functions...</p>
        </div>
        <v-expansion-panels v-else variant="accordion">
          <v-expansion-panel v-for="server in serversWithFunctions" :key="server.name" :disabled="!server.functions || server.functions.length === 0">
            <v-expansion-panel-title>
              <div class="d-flex justify-space-between align-center" style="width: 100%;">
                <span>{{ server.name }}</span>
                <v-chip
                  v-if="server.functions && server.functions.length > 0"
                  size="small"
                  :color="allFunctionsSelectedForServer(server.name) ? 'success' : 'grey'"
                  class="ml-2"
                >
                  {{ (localSelectedFunctions[server.name] || []).length }} / {{ server.functions.length }} selected
                </v-chip>
                <v-chip v-else size="small" color="warning" class="ml-2">
                  No functions available or failed to load
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text v-if="server.functions && server.functions.length > 0">
              <div class="mb-2">
                <v-btn size="small" @click="toggleSelectAllForServer(server.name, true)" class="mr-2">Select All</v-btn>
                <v-btn size="small" @click="toggleSelectAllForServer(server.name, false)">Deselect All</v-btn>
              </div>
              <v-list dense class="hide-scrollbar" style="max-height: 300px; overflow-y: auto;">
                <v-list-item v-for="func in server.functions" :key="func.name" density="compact">
                  <template v-slot:prepend>
                    <v-checkbox-btn
                      v-model="localSelectedFunctions[server.name]"
                      :value="func.name"
                      density="compact"
                    ></v-checkbox-btn>
                  </template>
                  <v-list-item-title>{{ func.name }}</v-list-item-title>
                  <v-list-item-subtitle :title="func.description" class="text-truncate">{{ func.description }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
         <div v-if="!loadingFunctions && serversWithFunctions.length === 0" class="text-center pa-4">
          <p>No tools selected or no functions available for the selected tools.</p>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" variant="elevated" @click="saveSelection">Save Functions</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { PropType } from 'vue'
import { mapGetters, mapActions, mapState } from 'vuex'
import { McpServer as StoreMcpServer, McpTool } from '@/store/mcp'
import { ToolMethod } from '@/types/mcp'

@Options({
  props: {
    show: { type: Boolean, required: true },
    selectedToolNames: { type: Array, required: true },
    initialSelectedFunctions: { type: Object, default: () => ({}) },
    overrideServerTools: { type: Object as PropType<Record<string, ToolMethod[]> | null>, default: null }
  },
  computed: {
    ...mapGetters(['enabledServers', 'mcpToolsForServer', 'mcpLoading'])
  },
  watch: {
    async show(newVal: boolean) { // Make watcher async
      if (newVal) {
        await this.initializeDialogState(); // Call the new class method
      }
    }
  }
})
export default class FunctionSelectionDialog extends Vue {
  show!: boolean
  selectedToolNames!: string[]
  initialSelectedFunctions!: { [serverName: string]: string[] }
  overrideServerTools!: Record<string, ToolMethod[]> | null

  // Vuex mapped Getters
  enabledServers!: StoreMcpServer[]
  mcpToolsForServer!: (serverId: string) => Record<string, McpTool>
  mcpLoading!: boolean

  localSelectedFunctions: { [serverName: string]: string[] } = {}
  serversWithFunctions: Array<{ name: string; functions: ToolMethod[] }> = []
  loadingFunctions = false // This is for the dialog's own loading state, distinct from mcpModuleLoading

  async initializeDialogState() {
    this.loadingFunctions = true; // Set loading state at the beginning

    // Ensure functions are loaded and this.serversWithFunctions is populated
    await this.loadFunctionsForSelectedTools(); 

    // Deep copy initial selections to avoid mutating the prop
    const newSelectionsState: { [serverName: string]: string[] } = 
      JSON.parse(JSON.stringify(this.initialSelectedFunctions || {}));

    // Ensure every server that will be displayed has a valid array for its selections
    this.serversWithFunctions.forEach((server: { name: string; functions: ToolMethod[] }) => {
      if (!newSelectionsState[server.name]) {
        newSelectionsState[server.name] = [];
      } else if (!Array.isArray(newSelectionsState[server.name])) {
        console.warn(
          `[FunctionSelectionDialog] Initial selection for server "${server.name}" was not an array. ` +
          `Received: ${JSON.stringify(newSelectionsState[server.name])}. Resetting to an empty array.`
        );
        newSelectionsState[server.name] = [];
      }
    });
    
    this.localSelectedFunctions = newSelectionsState;
    // Note: loadFunctionsForSelectedTools sets this.loadingFunctions = false at its end.
    // If loadFunctionsForSelectedTools might not run (e.g. !this.enabledServers), 
    // ensure loadingFunctions is set to false here too, or at the end of this method.
    // However, loadFunctionsForSelectedTools already handles setting it to false in all its exit paths.
  }

  async loadFunctionsForSelectedTools() {
    this.loadingFunctions = true
    this.serversWithFunctions = []

    // Shared-agent mode: caller pre-supplies the owner's tool catalog.
    // Skip the recipient-scoped Vuex/MCP path entirely.
    if (this.overrideServerTools) {
      const override = this.overrideServerTools
      this.serversWithFunctions = this.selectedToolNames.map(name => ({
        name,
        functions: override[name] ?? []
      }))
      this.loadingFunctions = false
      return
    }

    if (!this.enabledServers) {
      console.error("enabledServers is not available from Vuex store. Check mapping in component and store.")
      this.loadingFunctions = false
      return
    }

    for (const serverName of this.selectedToolNames) {
      const serverDetails = this.enabledServers.find(s => s.name === serverName)
      if (serverDetails && serverDetails.id) {
        const serverId = serverDetails.id
        try {
          const serverToolsMap: Record<string, McpTool> | undefined = await this.$store.dispatch('fetchMcpTools', serverId)
          
          if (serverToolsMap) {
            const functions: ToolMethod[] = Object.values(serverToolsMap).map(tool => {
              const properties: Record<string, { type: string, description: string }> = {}
              const required: string[] = []
              if (tool.parameters) {
                for (const param of tool.parameters) {
                  properties[param.name] = { type: param.type, description: param.description }
                  if (param.required) {
                    required.push(param.name)
                  }
                }
              }
              return {
                name: tool.name,
                description: tool.description,
                inputSchema: {
                  type: 'object',
                  properties: properties,
                  required: required
                }
              }
            })
            this.serversWithFunctions.push({ name: serverName, functions })
          } else {
            console.warn(`No tools returned from fetchMcpTools for serverId: ${serverId}`)
            this.serversWithFunctions.push({ name: serverName, functions: [] })
          }
        } catch (error: any) { // Added type for error
          console.error(`Failed to load functions for server ${serverName} (ID: ${serverId}):`, error)
          this.serversWithFunctions.push({ name: serverName, functions: [] })
        }
      } else {
        console.warn(`Server details or ID not found for server name: ${serverName} in enabledServers list.`)
        this.serversWithFunctions.push({ name: serverName, functions: [] })
      }
    }
    this.loadingFunctions = false
  }

  allFunctionsSelectedForServer(serverName: string): boolean {
    const server = this.serversWithFunctions.find(s => s.name === serverName)
    if (!server || !server.functions || server.functions.length === 0) return false
    const selected = this.localSelectedFunctions[serverName] || []
    return selected.length === server.functions.length
  }

  toggleSelectAllForServer(serverName: string, select: boolean) {
    const server = this.serversWithFunctions.find(s => s.name === serverName)
    if (server && server.functions) {
      if (select) {
        this.localSelectedFunctions[serverName] = server.functions.map(f => f.name)
      } else {
        this.localSelectedFunctions[serverName] = []
      }
    }
  }

  closeDialog() {
    this.$emit('update:modelValue', false)
  }

  saveSelection() {
    const finalSelectedFunctions: { [serverName: string]: string[] } = {}
    for (const serverName of this.selectedToolNames) {
      if (this.localSelectedFunctions[serverName]) {
        finalSelectedFunctions[serverName] = this.localSelectedFunctions[serverName]
      }
    }
    this.$emit('save', finalSelectedFunctions)
    this.closeDialog()
  }
}
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style>
